import translate from 'core/translate'
import { color } from 'theme'
import { AddToolId } from '../templateEditor.data'
import { EditorTheme, defaultEditorTheme } from '../templateEditor.theme'
import { PlaceModeOptions, PlaceToolId, Rect, ShapeKind, TemplateElement } from '../templateEditor.types'
import { frameSizeForImage, listItemCountForBox, listSizeFor, tableGridForBox, tableSizeFor } from './placementMetrics'
import { nextElementId } from './nextElementId'

export type CreateElementInput = {
    type: PlaceToolId
    box?: Rect
    center?: { x: number; y: number }
    opts?: PlaceModeOptions
    theme?: EditorTheme
    index?: number
}

const defaultListLabels = (count: number, horizontal: boolean) => {
    const label = horizontal ? translate('templateEditorDefaultListText') : translate('templateEditorDefaultListItem')
    return Array.from({ length: count }, (_, i) => `${label} ${i + 1}`)
}

export const resizeListItems = (items: string[], count: number, horizontal: boolean) => {
    const next = Math.max(1, count)
    if (items.length === next) {
        return items
    }
    if (items.length > next) {
        return items.slice(0, next)
    }

    const extras = defaultListLabels(next, horizontal).slice(items.length)
    return [...items, ...extras]
}

const defaultTableData = (rows: number, cols: number) =>
    Array.from({ length: rows }, (_, row) =>
        Array.from({ length: cols }, (_, col) =>
            row === 0 ? `${translate('templateEditorDefaultColumn')} ${col + 1}` : ''
        )
    )

/** Стандартный размер при одиночном клике. */
export const defaultSizeForPlaceType = (type: PlaceToolId, opts: PlaceModeOptions, theme: EditorTheme): Rect => {
    switch (type) {
        case 'text':
            return { x: 0, y: 0, w: 400, h: 60 }
        case 'shape': {
            const kind = opts.shapeKind ?? 'rect'
            if (kind === 'line') {
                return { x: 0, y: 0, w: 200, h: 4 }
            }
            return { x: 0, y: 0, w: 200, h: 120 }
        }
        case 'list': {
            const size = listSizeFor(1, { orientation: 'vertical' }, theme)
            return { x: 0, y: 0, w: size.w, h: size.h }
        }
        case 'listH': {
            const size = listSizeFor(1, { orientation: 'horizontal' }, theme)
            return { x: 0, y: 0, w: size.w, h: size.h }
        }
        case 'table': {
            const size = tableSizeFor(1, 1, theme)
            return { x: 0, y: 0, w: size.w, h: size.h }
        }
        case 'image':
        case 'video': {
            const frame = frameSizeForImage(opts.naturalW ?? 1600, opts.naturalH ?? 900)
            return { x: 0, y: 0, w: frame.w, h: frame.h }
        }
        default:
            return { x: 0, y: 0, w: 200, h: 120 }
    }
}

const resolveBox = (input: CreateElementInput, theme: EditorTheme): Rect => {
    if (input.box) {
        return input.box
    }

    const def = defaultSizeForPlaceType(input.type, input.opts ?? {}, theme)
    const center = input.center ?? { x: 400, y: 300 }

    return {
        x: center.x - def.w / 2,
        y: center.y - def.h / 2,
        w: def.w,
        h: def.h,
    }
}

export const createElement = (input: CreateElementInput): TemplateElement => {
    const theme = input.theme ?? defaultEditorTheme
    const box = resolveBox(input, theme)
    const zIndex = input.index ?? 1
    const opts = input.opts ?? {}

    switch (input.type) {
        case 'text':
            return {
                id: nextElementId('txt'),
                type: 'text',
                name: `${translate('templateEditorText')} ${zIndex}`,
                ...box,
                text: translate('templateEditorDefaultText'),
                fontSize: 32,
                color: color.fontDarkUltraHigh,
                align: 'left',
            }

        case 'shape': {
            const shapeKind: ShapeKind = opts.shapeKind ?? 'rect'
            const radius = shapeKind === 'rounded' ? 24 : 0
            const nameKey =
                shapeKind === 'rounded'
                    ? 'templateEditorRounded'
                    : shapeKind === 'ellipse'
                    ? 'templateEditorEllipse'
                    : shapeKind === 'line'
                    ? 'templateEditorLine'
                    : 'templateEditorRectangle'

            return {
                id: nextElementId('sh'),
                type: 'shape',
                name: translate(nameKey),
                ...box,
                shapeKind,
                fill: theme.demoFill,
                radius,
                ...(shapeKind === 'line'
                    ? {
                          lineEnds: opts.lineEnds ?? {
                              x1: 0,
                              y1: box.h / 2,
                              x2: box.w,
                              y2: box.h / 2,
                          },
                      }
                    : null),
            }
        }

        case 'list':
        case 'listH': {
            const orientation = input.type === 'listH' ? 'horizontal' : 'vertical'
            const base = { fontSize: 28, lineHeight: 1.5, gap: 8, orientation } as const
            const count = input.box ? listItemCountForBox(box.w, box.h, base, theme) : 1
            const fit = listSizeFor(count, base, theme)

            return {
                id: nextElementId('lst'),
                type: 'list',
                name:
                    orientation === 'horizontal'
                        ? translate('templateEditorAddHorizontalList')
                        : translate('templateEditorAddVerticalList'),
                x: box.x,
                y: box.y,
                w: orientation === 'horizontal' ? fit.w : Math.max(box.w, theme.minElSize),
                h: orientation === 'horizontal' ? Math.max(box.h, fit.h) : fit.h,
                items: defaultListLabels(count, orientation === 'horizontal'),
                orientation,
                fontSize: 28,
                gap: 8,
                indent: orientation === 'horizontal' ? 0 : 24,
                color: color.fontDarkUltraHigh,
                markerType: orientation === 'horizontal' ? 'none' : 'bullet',
            }
        }

        case 'table': {
            let rows = 1
            let cols = 1
            let w = box.w
            let h = box.h

            if (input.box) {
                const grid = tableGridForBox(box.w, box.h, theme)
                rows = grid.rows
                cols = grid.cols
                w = grid.w
                h = grid.h
            } else {
                const size = tableSizeFor(rows, cols, theme)
                w = size.w
                h = size.h
            }

            return {
                id: nextElementId('tbl'),
                type: 'table',
                name: `${translate('templateEditorTable')} ${zIndex}`,
                x: box.x,
                y: box.y,
                w,
                h,
                rows,
                cols,
                data: defaultTableData(rows, cols),
                rowHeight: theme.rowHeight,
                colWidth: theme.tableColWidth,
                rowHeights: Array.from({ length: rows }, () => theme.rowHeight),
                colWidths: Array.from({ length: cols }, () => theme.tableColWidth),
                borderColor: color.borderDarkTertiary,
                cellPadding: 8,
                zebra: true,
            }
        }

        case 'image':
            return {
                id: nextElementId('img'),
                type: 'image',
                name: opts.name || translate('templateEditorImage'),
                ...box,
                src: opts.src ?? '',
                sourceId: opts.sourceId,
                thumbnail: opts.thumbnail,
                objectFit: 'contain',
                naturalW: opts.naturalW,
                naturalH: opts.naturalH,
            }

        case 'video':
            return {
                id: nextElementId('vid'),
                type: 'video',
                name: opts.name || translate('templateEditorVideo'),
                ...box,
                src: opts.src ?? '',
                sourceId: opts.sourceId,
                thumbnail: opts.thumbnail,
                objectFit: 'contain',
                naturalW: opts.naturalW,
                naturalH: opts.naturalH,
            }

        default: {
            const _exhaustive: never = input.type
            return _exhaustive
        }
    }
}

export const isAddToolId = (type: PlaceToolId): type is AddToolId =>
    type === 'text' || type === 'shape' || type === 'list' || type === 'listH' || type === 'table'

export const elementTypeFromFile = (fileType: string): PlaceToolId | null => {
    if (fileType === 'image') return 'image'
    if (fileType === 'video') return 'video'
    return null
}
